import React, { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { Modal } from '../../components/Modal/Modal'
import { useInviteUserMutation, useLazyGetInvitesQuery } from '../../store/auth/auth'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { CustomerFreeSeatsResponse, useGetBillingCustomerFreeSeatsQuery } from '../../store/plan/plan'
import { Button } from '../../components/Button/Button'
import { SelectDropdown } from '../../components/SelectDropdown/SelectDropdown'
import { getInviteRoleGroups } from './InviteRoleGroups'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { getUserName } from '../utils'
import { AvailableSeatsInfo } from './AvailableSeatsInfo'
import { LinkButton } from '../../components/Button/LinkButton'
import { useDebouncedCallback } from 'use-debounce'
import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { defaultPendingInvitationsFilter } from './PendingInvitations/utils'

export interface InviteUserModalProps {
  onHide: () => void
}

type FormValues = {
  firstName: string
  lastName: string
  email: string
  roleGroupName: string
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({ onHide }) => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const inviteRoleGroups = useMemo(() => getInviteRoleGroups({ onlyInvitable: true }), [])
  const [hasValidationErrors, setValidationErrors] = useState(false)
  const {
    data: freeSeatsData,
    isFetching: isFreeSeatsFetching,
    isError: isFreeSeatsFetchingError,
  } = useGetBillingCustomerFreeSeatsQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    roleGroupName: getInviteRoleGroups({ onlyInvitable: true })[0].name,
  }

  const [inviteUser, { isLoading: isInviteLoading, isSuccess }] = useInviteUserMutation()

  const handleSave = tracked(
    (data: FormValues) => {
      inviteUser({
        accountId,
        userData: {
          firstName: data.firstName,
          lastName: data.lastName,
          name: getUserName(data.firstName, data.lastName) || undefined,
          email: data.email,
          roleIds: inviteRoleGroups.find((group) => group.name === data.roleGroupName)?.roleIds || [],
        },
      })
    },
    { eventName: TrackEvent.INVITE_USER_SENT },
    {
      eventDetailsExtractor: (data: FormValues) => {
        return {
          email: data.email,
        }
      },
    },
  )

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit, reset, control } = form
  const selectedRoleGroupName = useWatch({ control, name: 'roleGroupName' })

  useEffect(() => {
    if (isSuccess) {
      onHide()
      reset()
    }
  }, [isSuccess, onHide, reset])

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Invite User</h4>
        </Col>
      </Row>

      <Row>
        <FormProvider {...form}>
          <InviteUserForm
            freeSeatsData={freeSeatsData?.data}
            isFreeSeatsFetching={isFreeSeatsFetching}
            isFreeSeatsFetchingError={isFreeSeatsFetchingError}
            setValidationErrors={setValidationErrors}
          />
        </FormProvider>
      </Row>
    </Container>
  )

  const hasAvailableSeat = () => {
    return !isFreeSeatsFetching
      ? selectedRoleGroupName === 'Administrative Assistant' || selectedRoleGroupName === 'Administrative Assistant with Templates'
        ? freeSeatsData?.data.seats.sidecar.available !== 0
        : freeSeatsData?.data.seats.general.available !== 0
      : false
  }

  const footer = (
    <div className='d-flex gap-3'>
      <Button variant='secondary' onClick={onHide}>
        Cancel
      </Button>
      <ScopedBlock scope={{ scope: 'consoleuser:invite', superScope: ['consoleuser:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
        <ScopedBlock
          tooltipText='Buy more seats to add users'
          tooltipButtonText='Buy more seats'
          authActions={[hasAvailableSeat]}
          missingScopeAction={MissingScopeAction.UPGRADE}
        >
          <Button
            variant='primary'
            onClick={handleSubmit(handleSave)}
            loading={isInviteLoading}
            title={!hasAvailableSeat() ? 'You need to buy more seats to invite more users' : ''}
            disabled={hasValidationErrors}
          >
            Invite
          </Button>
        </ScopedBlock>
      </ScopedBlock>
    </div>
  )

  return <Modal show onHide={onHide} header={<></>} body={body} footer={footer} centered={true} />
}

interface InviteUserFormProps {
  freeSeatsData?: CustomerFreeSeatsResponse
  isFreeSeatsFetching: boolean
  isFreeSeatsFetchingError: boolean
  setValidationErrors: (hasErrors: boolean) => void
}

const InviteUserForm = ({ freeSeatsData, isFreeSeatsFetching, isFreeSeatsFetchingError, setValidationErrors }: InviteUserFormProps) => {
  const { control } = useFormContext<FormValues>()
  const accountId = useAppSelector(selectAccountId)
  const inviteRoleGroups = useMemo(() => getInviteRoleGroups({ onlyInvitable: true }), [])
  const [triggerGetFilteredInvites, query] = useLazyGetInvitesQuery()

  const invitationStateOfEmail = query.data && Array.isArray(query.data.data) && query.data.data.length > 0 ? query.data.data[0].state : null

  const handleEmailChanged = useDebouncedCallback((email: string) => {
    triggerGetFilteredInvites({
      accountId,
      filter: {
        ...defaultPendingInvitationsFilter,
        emailPattern: email,
      },
      pageRequest: {
        page: 0,
        size: DEFAULT_PAGE_SIZE,
      },
    })
  }, 500)

  useEffect(() => {
    setValidationErrors(invitationStateOfEmail !== null)
  }, [invitationStateOfEmail, setValidationErrors])

  const hasNoAvailableSeat = freeSeatsData ? freeSeatsData.seats.general.available === 0 && freeSeatsData.seats.sidecar.available === 0 : true

  return (
    <>
      <Container className='d-flex flex-column gap-4 p-0'>
        <Row>
          <Col>
            <div>
              <div>
                <AvailableSeatsInfo seats={freeSeatsData?.seats} isFetching={isFreeSeatsFetching} isError={isFreeSeatsFetchingError} />
              </div>
              <div className='d-inline-flex pt-2'>
                <LinkButton variant='primary' to='/account/plan'>
                  Buy more
                </LinkButton>
              </div>
            </div>
          </Col>
        </Row>
        {!hasNoAvailableSeat ? (
          <>
            <Row>
              <Col>
                <Controller
                  name='email'
                  control={control}
                  rules={{
                    required: 'Email address is required.',
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: 'Entered value does not match email format',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <>
                      <Form.Label>Email address*</Form.Label>
                      <Form.Control
                        {...field}
                        id={field.name}
                        type='email'
                        placeholder='Enter email address…'
                        onChange={(event) => {
                          field.onChange(event)
                          handleEmailChanged(event.currentTarget.value)
                        }}
                        isInvalid={!!fieldState.error || invitationStateOfEmail !== null}
                      />
                      {fieldState.error || invitationStateOfEmail ? (
                        <FormFeedback>
                          {fieldState.error?.message ?? (invitationStateOfEmail ? `${invitationStateOfEmail} invitation already exists` : '')}
                        </FormFeedback>
                      ) : null}
                    </>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name='firstName'
                  control={control}
                  render={({ field }) => (
                    <>
                      <Form.Label>First name</Form.Label>
                      <Form.Control id={field.name} type='text' placeholder='Enter first name…' {...field} />
                    </>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name='lastName'
                  control={control}
                  render={({ field }) => (
                    <>
                      <Form.Label>Last name</Form.Label>
                      <Form.Control id={field.name} type='text' placeholder='Enter last name…' {...field} />
                    </>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name='roleGroupName'
                  control={control}
                  render={({ field, fieldState }) => (
                    <>
                      <Form.Label>Role</Form.Label>
                      <SelectDropdown
                        className='zocks-px-0.75 py-2'
                        options={inviteRoleGroups.map((roleGroup) => {
                          return { id: roleGroup.name, label: roleGroup.name }
                        })}
                        initialSelectedOption={inviteRoleGroups
                          .map((roleGroup) => {
                            return { id: roleGroup.name, label: roleGroup.name }
                          })
                          .find((option) => option.id === field.value)}
                        onOptionSelected={(option) => field.onChange(option.id)}
                      />
                      {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                    </>
                  )}
                />
              </Col>
            </Row>
          </>
        ) : null}
      </Container>
    </>
  )
}
